import './IsfahanInventory.css'
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Table from "../../components/Table/table";
import Loading from "../../components/Loading/loading";
import { Link } from 'react-router-dom';
import { utils } from "react-modern-calendar-datepicker";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import "react-multi-date-picker/styles/layouts/mobile.css";

const INVENTORY_API_URL ="/api/getisfahaninventorydata";
const CURRENCY_API_URL ="/api/getcurrencypricesdata";


function toFarsiDigits(num) {
  if (num === null || num === undefined) return "";
  const farsiDigits = ['۰','۱','۲','۳','۴','۵','۶','۷','۸','۹'];
  return num.toString().replace(/[0-9]/g, x => farsiDigits[x]);
}

const IsfahanInventory = () => {
  const [data, setData] = useState({ milgerd: [], tirahan: [] });
  const [lastCurrency, setLastCurrency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tracks which SIZES are expanded to show unpriced items
  // Format: "milgerd-14" or "tirahan-18"
  const [sizesShowingUnpriced, setSizesShowingUnpriced] = useState(new Set()); 
  const [selectedDay, setSelectedDay] = useState(utils("fa").getToday()); 

  // --- 1. Helper: Format Date ---
  const formatToAPIDate = (jalaliDate) =>
    `${jalaliDate.year}-${String(jalaliDate.month).padStart(2, "0")}-${String(
      jalaliDate.day
    ).padStart(2, "0")}`;

  // --- 2. Logic: Process Data (Grouping & Filtering) ---
  const processProducts = useCallback((products, productType) => {
    // Return empty structure if no data
    if (!products || products.length === 0) {
      return { productsBySize: {}, sortedSizes: [], sizeClickability: {} };
    }

    const sortByTitle = (a, b) => (a.title || "").localeCompare(b.title || "");

    // A. Separate Priced vs Unpriced
    const pricedProducts = products
      .filter((p) => p.price && Number(p.price) !== 0)
      .sort(sortByTitle);
      
    const unpricedProducts = products
      .filter((p) => !p.price || Number(p.price) === 0)
      .sort(sortByTitle);

    // B. Group by Size
    const groupBySize = (items) =>
      items.reduce((acc, product) => {
        const size = product.size || "Unsized";
        if (!acc[size]) acc[size] = [];
        acc[size].push(product);
        return acc;
      }, {});

    const pricedGrouped = groupBySize(pricedProducts);
    const unpricedGrouped = groupBySize(unpricedProducts);

    // C. Get all unique sizes and sort them numerically
    const allSizes = new Set([
      ...Object.keys(pricedGrouped),
      ...Object.keys(unpricedGrouped),
    ]);

    const sortedSizes = Array.from(allSizes).sort((a, b) => {
      if (a === "Unsized") return 1;
      if (b === "Unsized") return -1;
      return Number(a) - Number(b);
    });
    
    // D. Combine lists based on toggle state
    const combinedProductsBySize = {};
    const effectiveSizes = new Set();
    const sizeClickability = {}; 
    
    sortedSizes.forEach((size) => {
      const key = `${productType}-${size}`;
      const priced = pricedGrouped[size] || [];
      const unpriced = unpricedGrouped[size] || [];
      
      const isUnpricedToggledOn = sizesShowingUnpriced.has(key);
      const hasPriced = priced.length > 0;
      
      // Mark as clickable only if it has priced items (to toggle unpriced ones)
      sizeClickability[size] = hasPriced; 
      
      let products;
      if (hasPriced) {
          // If priced items exist, toggle determines if unpriced are shown
          products = isUnpricedToggledOn ? [...priced, ...unpriced] : priced;
      } else {
          // If ONLY unpriced items exist, always show them
          products = unpriced;
      }

      combinedProductsBySize[size] = products;
      
      if (products.length > 0) {
        effectiveSizes.add(size);
      }
    });

    return { 
        productsBySize: combinedProductsBySize, 
        sortedSizes: sortedSizes.filter(size => effectiveSizes.has(size)),
        sizeClickability: sizeClickability 
    };
  }, [sizesShowingUnpriced]);

  // --- 3. Memoized Data Processing ---
  const milgerdProcessed = useMemo(() => {
      return processProducts(data.milgerd, 'milgerd');
  }, [data.milgerd, processProducts]);

  const tirahanProcessed = useMemo(() => {
      return processProducts(data.tirahan, 'tirahan');
  }, [data.tirahan, processProducts]);

  // --- 4. Handler: Table Title Click ---
  const handleTitleClick = (productType, size) => {
      const key = `${productType}-${size}`;
      setSizesShowingUnpriced(prevSizes => {
          const newSet = new Set(prevSizes);
          if (newSet.has(key)) {
              newSet.delete(key);
          } else {
              newSet.add(key);
          }
          return newSet;
      });
  }

  // --- 5. Effect: Fetch Currency (Every 60 Seconds) ---
  useEffect(() => {
    const fetchCurrency = () => {
      fetch(CURRENCY_API_URL)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.length > 0) setLastCurrency(data[data.length - 1]);
        })
        .catch(console.error);
    };

    fetchCurrency(); 
    const interval = setInterval(fetchCurrency, 60000); 
    return () => clearInterval(interval);
  }, []);

  // --- 6. Effect: Fetch Inventory (Date Change OR Every 60 Seconds) ---
  useEffect(() => {
    if (!selectedDay) return;

    // isBackground = true means "Silent Update" (no spinner)
    const fetchInventory = async (isBackground = false) => {
      if (!isBackground) setIsLoading(true);
      setError(null);
      
      const formattedDate = formatToAPIDate(selectedDay);
      const urlWithDate = `${INVENTORY_API_URL}?date=${formattedDate}`;
      
      try {
        const response = await fetch(urlWithDate);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jsonData = await response.json();
        setData(jsonData);
      } catch (e) {
        console.error("Failed to fetch data:", e);
        // Only block UI with error if it's the user's manual action
        if (!isBackground) {
            setError("Failed to load inventory data for the selected date.");
            setData({ milgerd: [], tirahan: [] });
        }
      } finally {
        if (!isBackground) setIsLoading(false);
      }
    };

    // A. Initial Load (Shows Spinner)
    fetchInventory(false);

    // B. Interval Load (Silent - Every 60s)
    const interval = setInterval(() => fetchInventory(true), 60000);

    // C. Cleanup
    return () => clearInterval(interval);
  }, [selectedDay]);

  // --- 7. Handler: Date Picker ---
  const handleDateChange = (dateObject) => {
    if (!dateObject) return;
    setSelectedDay({
      year: Number(dateObject.year),
      month: Number(dateObject.month.number || dateObject.month),
      day: Number(dateObject.day),
    });
  };
  
  // --- Rendering ---
  if (isLoading) return <Loading />;
  if (error && (!data.milgerd?.length && !data.tirahan?.length)) {
    return <p className="dashboard-message error">{error}</p>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Link to="/home" className="header-link">خانه</Link>
        <p className="page-title">موجودی انبار اصفهان</p>
        
        <div className="header-right-group">
          <div className="date-picker-wrapper">
            <DatePicker
              value={`${selectedDay.year}/${selectedDay.month}/${selectedDay.day}`}
              onChange={handleDateChange}
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right" 
              inputClass="custom-date-input" 
            />
          </div>
        </div>
      </div>

      {error && <p className="dashboard-message error-inline">{error}</p>}

      {/* --- Section: Milgerd --- */}
      <section>
        <h2 className="section-title">میلگرد</h2>
        {milgerdProcessed.sortedSizes.length === 0 ? (
          <p className="dashboard-message">هیچ کالایی برای تاریخ انتخاب شده یافت نشد.</p>
        ) : (
          <div className="grid-container">
            {milgerdProcessed.sortedSizes.map((size) => (
              <Table
                key={`milgerd-${size}`}
                title={"سایز: " + toFarsiDigits(size)}
                products={milgerdProcessed.productsBySize[size]}
                
                // Only pass click handler if the size is actually clickable (has priced items)
                onTitleClick={
                  milgerdProcessed.sizeClickability[size]
                    ? () => handleTitleClick('milgerd', size)
                    : undefined
                } 
                isUnpricedShown={sizesShowingUnpriced.has(`milgerd-${size}`)}
              />
            ))}
          </div>
        )}
      </section>

      {/* --- Section: Tirahan --- */}
      <section>
        <h2 className="section-title">تیرآهن</h2>
        {tirahanProcessed.sortedSizes.length === 0 ? (
          <p className="dashboard-message">هیچ کالایی برای تاریخ انتخاب شده یافت نشد.</p>
        ) : (
          <div className="grid-container">
            {tirahanProcessed.sortedSizes.map((size) => (
              <Table
                key={`tirahan-${size}`}
                title={"سایز: " + toFarsiDigits(size)}
                products={tirahanProcessed.productsBySize[size]}

                // Only pass click handler if the size is actually clickable
                onTitleClick={
                  tirahanProcessed.sizeClickability[size]
                    ? () => handleTitleClick('tirahan', size)
                    : undefined
                } 
                isUnpricedShown={sizesShowingUnpriced.has(`tirahan-${size}`)}
              />
            ))}
          </div>
        )}
      </section>
      
      <div className="floating-footer">
        <p className="pill">
          سکه: {lastCurrency ? toFarsiDigits((lastCurrency.gold_coin_price / 10).toLocaleString()) : "..."}
        </p>
        <p className="pill">
          دلار: {lastCurrency ? toFarsiDigits((lastCurrency.dollar_price / 10).toLocaleString()) : "..."}
        </p>
        <p className="pill">
          طلا: {lastCurrency ? toFarsiDigits((lastCurrency.gold_gram_price / 10).toLocaleString()) : "..."}
        </p>
        <p className="pill">
          بورس: {lastCurrency ? toFarsiDigits(lastCurrency.stock_index.toLocaleString()) : "..."}
        </p>
        <p className="pill pill-ons">
          انس: {lastCurrency && lastCurrency.ounce_price ? toFarsiDigits(lastCurrency.ounce_price.toLocaleString()) : "..."}
        </p>
      </div>
    </div>
  );
};

export default IsfahanInventory;